import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { PlayButton } from "@util/assets";
import { colors } from "@util/constants";

interface Props {
  video: string;
  isActive?: boolean;
}

const VideoContainer = styled.div<{ isActive?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 320px;
  transition: all 0.3s ease;
  opacity: ${props => (props.isActive ? 1 : 0.5)};
  overflow: hidden;
  @media screen and (max-width: 1440px) {
    min-height: 272px;
  }
`;

const VideoThumbnailImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButtonWrapper = styled.button`
  svg {
    height: 59px;
    width: 59px;
  }
`;

const FullscreenOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const VideoWrapper = styled.div<{ isVisible: boolean }>`
  transform: ${props => (props.isVisible ? "scale(1)" : "scale(0.8)")};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: transform 0.4s ease, opacity 0.3s ease;
  position: relative;
  width: 75vw;
  height: 0;
  padding-bottom: 50.625%; /* 90vw * 9/16 = 50.625% */
  margin: 0 auto;
  @media screen and (max-width: 1440px) {
    width: 100%;
    padding-bottom: 56.25%;
  }
`;

const StyledIframe = styled.iframe<{ backgroundImage: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  outline: none;
  border: none;
  opacity: 1;
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-size: 99.8%;
  background-position: center;
  background-repeat: no-repeat;
  @media screen and (max-width: 576px) {
    width: 100%;
    height: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 30px;
  right: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.white};
  font-size: 18px;
  opacity: 1;
  svg {
    transform: translateY(5px);
    margin-right: 5px;
    font-size: 25px;
  }
  &:hover {
    opacity: 0.5;
  }
  @media screen and (max-width: 1800px) {
   top:20px
  }
  @media screen and (max-width: 1440px) {
    top: -50px;
  }
  @media screen and (max-width: 1024px) {
    right: 0;
  }
`;

const VideoModal = ({ video, isActive }: Props) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video}`)
      .then(res => res.json())
      .then(data => {
        const thumbBase = data.thumbnail_url.replace(/_[0-9]+x?[0-9]*\.jpg$/, "");
        setThumbnail(`${thumbBase}_1920x1080.jpg`);
      });
  }, [video]);

  const videoUrl = `https://player.vimeo.com/video/${video}?autoplay=1&muted=1&playsinline=1&controls=1`;

  const handlePlay = () => {
    setIsFullscreen(true);
    setTimeout(() => setIsContentVisible(true), 50);
  };

  const handleClose = () => {
    setIsContentVisible(false);
    setTimeout(() => {
      setIsFullscreen(false);
    }, 300);
  };

  const modalRoot =
    typeof document !== "undefined" ? document.getElementById("modal-root") || document.body : null;

  return (
    <>
      <VideoContainer isActive={isActive}>
        <VideoThumbnailImg src={thumbnail} alt="Video thumbnail" />
        {isActive && (
          <PlayButtonWrapper>
            <PlayButton height="59px" onClick={handlePlay} />
          </PlayButtonWrapper>
        )}
      </VideoContainer>

      {isFullscreen &&
        modalRoot &&
        createPortal(
          <FullscreenOverlay isVisible={isFullscreen}>
            <VideoWrapper isVisible={isContentVisible}>
              <StyledIframe
                src={videoUrl}
                backgroundImage={thumbnail}
                title="Vimeo Video"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
              <CloseButton onClick={handleClose}>
                {" "}
                <AiOutlineCloseCircle />
                Close
              </CloseButton>
            </VideoWrapper>
          </FullscreenOverlay>,
          modalRoot,
        )}
    </>
  );
};

export default VideoModal;
